import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { RefreshCw } from "lucide-react";

// Poll interval: 60 seconds is enough — we're not chasing hot patches, just
// making sure stale iPhone home-screen shortcuts pick up new deploys within
// a minute of the user opening the app.
const POLL_INTERVAL_MS = 60_000;

/**
 * Shows a small "New version available — tap to refresh" banner when the
 * backend's boot fingerprint changes. Rendered once at the app root.
 *
 * Design goals:
 *   - Zero visible UI when versions match (99% of the time).
 *   - Non-intrusive: single top banner, dismissible-by-refresh.
 *   - Safe fallback: if /api/version 404s (older backend), silently no-op.
 *   - Reload is a full-page reload, bypassing SPA client-side cache.
 */
export default function VersionCheckBanner() {
    const [staleVersion, setStaleVersion] = useState(null); // new version string that differs from ours
    const initialVersion = useRef(null);

    useEffect(() => {
        let cancelled = false;

        async function check() {
            try {
                const { data } = await api.get("/version", { params: { _t: Date.now() } });
                if (cancelled) return;
                const current = data?.version;
                if (!current) return;
                if (initialVersion.current == null) {
                    initialVersion.current = current;
                    return;
                }
                if (current !== initialVersion.current) {
                    setStaleVersion(current);
                }
            } catch {
                // Endpoint missing or offline — silently skip. We'll try again on next tick.
            }
        }

        // Fire once immediately, then poll.
        check();
        const t = setInterval(check, POLL_INTERVAL_MS);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    if (!staleVersion) return null;

    function hardReload() {
        // Best-effort cache clear then hard reload. On iOS Safari the standalone
        // web app just re-requests everything from the network with our
        // no-store meta tags in place.
        try { window.localStorage.removeItem("co_last_version"); } catch { /* noop */ }
        // Force a fresh navigation (no cached document).
        const url = new URL(window.location.href);
        url.searchParams.set("_v", staleVersion);
        window.location.replace(url.toString());
    }

    return (
        <div
            className="fixed inset-x-0 top-0 z-[100] bg-[#0E3B2E] text-white px-4 py-2 flex items-center justify-center gap-3 text-sm shadow-lg"
            data-testid="version-update-banner"
            role="alert"
        >
            <RefreshCw className="h-4 w-4 shrink-0" />
            <span>A new version of ClientOps is ready.</span>
            <button
                type="button"
                onClick={hardReload}
                className="rounded-full bg-white text-[#0E3B2E] font-medium px-3 py-1 text-xs hover:bg-[#F5F4EF] transition-colors"
                data-testid="version-update-refresh"
            >
                Tap to refresh
            </button>
        </div>
    );
}
